import { useEffect, useMemo, useState } from "react";
import { api, fmtDate, fmtINR } from "@/lib/api";
import { useAuth, hasRole } from "@/lib/auth";
import { PageHeader } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Warning, MapPin } from "@phosphor-icons/react";
import { Field } from "./Employees";
import { toast } from "sonner";

const ASSET_STATUS = ["available","assigned","in_transit","under_repair","damaged","lost","retired"];
const DEMO_STATUS = ["available","assigned","under_demo","returned_to_oem","lost","damaged"];
// "available" is displayed as "At Office" per business rule.
const statusLabel = (s) => (s === "available" || s === "at_office") ? "At Office" : (s || "").replace(/_/g, " ");
const todayStr = () => new Date().toISOString().slice(0,10);
const isOverdue = (a) => a?.status === "assigned" && a?.expected_return_date && a.expected_return_date < todayStr();

export default function Inventory() {
  const { user } = useAuth();
  const canManage = hasRole(user, "super_admin", "hr"); // add + assign + return
  const canDelete = hasRole(user, "super_admin");       // delete only
  const canManageDemo = canManage || user.role === "sales_manager";
  const [tab, setTab] = useState("assets");
  const [assets, setAssets] = useState([]);
  const [demo, setDemo] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [opportunities, setOpportunities] = useState([]);
  const [oems, setOems] = useState([]);
  const [openA, setOpenA] = useState(false);
  const [openD, setOpenD] = useState(false);
  const [openAssign, setOpenAssign] = useState(false);
  const [assignTarget, setAssignTarget] = useState(null);
  const [editingA, setEditingA] = useState(null);
  const [editingD, setEditingD] = useState(null);
  const [filter, setFilter] = useState("");
  const emptyA = { product_code: "", product_name: "", serial_number: "", model_number: "", oem: "", entry_date: "", purchase_date: "", warranty_expiry: "", asset_value: 0, remarks: "", status: "available" };
  const emptyD = { equipment_name: "", oem: "", product_code: "", serial_number: "", model_number: "", received_date: "", received_from: "", contact_person: "", contact_number: "", expected_return_date: "", remarks: "", status: "available" };
  const emptyAssign = { holder_type: "office", employee_id: "", account_id: "", opportunity_id: "", issue_date: todayStr(), expected_return_date: "", purpose: "", remarks: "" };
  const [formA, setFormA] = useState(emptyA);
  const [formD, setFormD] = useState(emptyD);
  const [formAssign, setFormAssign] = useState(emptyAssign);

  const load = () => {
    api.get("/inventory/assets").then((r) => setAssets(r.data));
    api.get("/inventory/demo").then((r) => setDemo(r.data));
  };
  useEffect(() => {
    load();
    api.get("/employees/lookup/all").then((r) => setEmployees(r.data));
    api.get("/accounts").then((r) => setAccounts(r.data));
    api.get("/opportunities").then((r) => setOpportunities(r.data));
    api.get("/oems").then((r) => setOems(r.data));
  }, []);

  const saveA = async () => {
    if (!formA.product_name || !formA.serial_number || !formA.product_code) return toast.error("Product code/name/serial required");
    try {
      if (editingA) await api.put(`/inventory/assets/${editingA.id}`, formA);
      else await api.post("/inventory/assets", formA);
      toast.success("Saved"); setOpenA(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const saveD = async () => {
    if (!formD.equipment_name || !formD.serial_number || !formD.oem) return toast.error("Name, serial and OEM required");
    try {
      if (editingD) await api.put(`/inventory/demo/${editingD.id}`, formD);
      else await api.post("/inventory/demo", formD);
      toast.success("Saved"); setOpenD(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const openAssignDialog = (a) => {
    setAssignTarget(a);
    setFormAssign({ ...emptyAssign });
    setOpenAssign(true);
  };
  const submitAssign = async () => {
    if (!formAssign.issue_date) return toast.error("Issue date required");
    if (!formAssign.purpose?.trim()) return toast.error("Purpose is required");
    if (formAssign.holder_type === "employee" && !formAssign.employee_id) return toast.error("Pick an employee");
    try {
      const body = {
        holder_type: formAssign.holder_type,
        employee_id: formAssign.holder_type === "employee" ? formAssign.employee_id : null,
        account_id: formAssign.account_id || null,
        opportunity_id: formAssign.opportunity_id || null,
        issue_date: formAssign.issue_date,
        expected_return_date: formAssign.holder_type === "office" ? null : (formAssign.expected_return_date || null),
        purpose: formAssign.purpose || null,
        remarks: formAssign.remarks || null,
      };
      await api.post(`/inventory/assets/${assignTarget.id}/assign`, body);
      toast.success("Assignment saved"); setOpenAssign(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const returnAsset = async (a) => {
    const condition = window.prompt("Condition (good/damaged/missing_parts/faulty)", "good") || "good";
    try { await api.post(`/inventory/assets/${a.id}/return`, { return_date: todayStr(), condition }); toast.success("Returned"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const delA = async (a) => { if (window.confirm("Delete asset?")) { await api.delete(`/inventory/assets/${a.id}`); toast.success("Deleted"); load(); } };
  const delD = async (d) => { if (window.confirm("Delete demo equipment?")) { await api.delete(`/inventory/demo/${d.id}`); toast.success("Deleted"); load(); } };
  const returnToOEM = async (d) => {
    const returned_to_oem = window.prompt("OEM Name") || d.oem;
    const courier = window.prompt("Courier name") || "";
    const tracking = window.prompt("Tracking number") || "";
    try { await api.post(`/inventory/demo/${d.id}/return-to-oem`, { return_date: todayStr(), returned_to_oem, courier_name: courier, tracking_number: tracking }); toast.success("Returned to OEM"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const filteredAssets = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return assets;
    return assets.filter((a) =>
      [a.product_name, a.product_code, a.serial_number, a.oem, a.status]
        .some((v) => v && String(v).toLowerCase().includes(q))
    );
  }, [assets, filter]);
  const accountById = (id) => accounts.find((x) => x.id === id)?.company_name || "";
  const opportunityById = (id) => opportunities.find((x) => x.id === id)?.name || "";

  const holderLabel = (a) => {
    if (a.status === "at_office") return "At Office";
    if (a.status === "assigned") {
      const emp = employees.find((e) => e.id === a.assigned_to)?.full_name || "Employee";
      return emp;
    }
    return "—";
  };

  return (
    <div data-testid="inventory-page">
      <PageHeader title="Inventory" description="Manage company assets and OEM demo equipment." />
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="assets" data-testid="inv-tab-assets">Assets ({assets.length})</TabsTrigger>
          <TabsTrigger value="demo" data-testid="inv-tab-demo">Demo Equipments ({demo.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="assets" className="mt-4 space-y-3">
          <div className="flex flex-wrap items-center gap-2 justify-between">
            <Input placeholder="Filter by product, code, serial, OEM, status…" value={filter} onChange={(e) => setFilter(e.target.value)} className="max-w-md" data-testid="inv-filter" />
            {canManage && (
              <Dialog open={openA} onOpenChange={setOpenA}>
                <DialogTrigger asChild><Button className="btn-brand" onClick={() => { setEditingA(null); setFormA(emptyA); }} data-testid="asset-new"><Plus size={16} className="mr-1.5" />New Asset</Button></DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader><DialogTitle>{editingA ? "Edit" : "New"} Asset</DialogTitle></DialogHeader>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Product Code *"><Input value={formA.product_code} onChange={(e) => setFormA({ ...formA, product_code: e.target.value })} /></Field>
                    <Field label="Product Name *"><Input value={formA.product_name} onChange={(e) => setFormA({ ...formA, product_name: e.target.value })} /></Field>
                    <Field label="Serial Number *"><Input value={formA.serial_number} onChange={(e) => setFormA({ ...formA, serial_number: e.target.value })} /></Field>
                    <Field label="Model Number"><Input value={formA.model_number || ""} onChange={(e) => setFormA({ ...formA, model_number: e.target.value })} /></Field>
                    <Field label="OEM">
                      <Select value={formA.oem || "none"} onValueChange={(v) => setFormA({ ...formA, oem: v === "none" ? "" : v })}>
                        <SelectTrigger><SelectValue placeholder="Pick OEM" /></SelectTrigger>
                        <SelectContent><SelectItem value="none">— None —</SelectItem>{oems.map((o) => <SelectItem key={o.id} value={o.name}>{o.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </Field>
                    <Field label="Status">
                      <Select value={formA.status} onValueChange={(v) => setFormA({ ...formA, status: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>{ASSET_STATUS.map((s) => <SelectItem key={s} value={s}>{statusLabel(s)}</SelectItem>)}</SelectContent>
                      </Select>
                    </Field>
                    <Field label="Entry Date"><Input type="date" value={formA.entry_date || ""} onChange={(e) => setFormA({ ...formA, entry_date: e.target.value })} /></Field>
                    <Field label="Purchase Date"><Input type="date" value={formA.purchase_date || ""} onChange={(e) => setFormA({ ...formA, purchase_date: e.target.value })} /></Field>
                    <Field label="Warranty Expiry"><Input type="date" value={formA.warranty_expiry || ""} onChange={(e) => setFormA({ ...formA, warranty_expiry: e.target.value })} /></Field>
                    <Field label="Asset Value (₹)"><Input type="number" value={formA.asset_value || 0} onChange={(e) => setFormA({ ...formA, asset_value: Number(e.target.value) })} /></Field>
                    <div className="col-span-2"><Field label="Remarks"><Textarea value={formA.remarks || ""} onChange={(e) => setFormA({ ...formA, remarks: e.target.value })} /></Field></div>
                  </div>
                  <DialogFooter><Button variant="outline" onClick={() => setOpenA(false)}>Cancel</Button><Button className="btn-brand" onClick={saveA} data-testid="asset-save">Save</Button></DialogFooter>
                </DialogContent>
              </Dialog>
            )}
          </div>

          <Card className="p-0 overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Product</TableHead>
                  <TableHead>Serial</TableHead>
                  <TableHead>OEM</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Current Holder</TableHead>
                  <TableHead>Issue / Return</TableHead>
                  <TableHead>Value</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredAssets.map((a) => {
                  const overdue = isOverdue(a);
                  return (
                    <TableRow key={a.id} className={overdue ? "bg-red-50/40 dark:bg-red-950/20" : ""}>
                      <TableCell><div className="font-medium">{a.product_name}</div><div className="text-xs font-mono opacity-60">{a.product_code}</div></TableCell>
                      <TableCell className="font-mono text-xs">{a.serial_number}</TableCell>
                      <TableCell>{a.oem || "—"}</TableCell>
                      <TableCell>
                        <span className={`text-xs px-2 py-0.5 rounded-full capitalize ${overdue ? "bg-red-100 text-red-700" : a.status === "available" ? "bg-green-100 text-green-700" : a.status === "at_office" ? "bg-blue-100 text-blue-700" : "bg-slate-100 dark:bg-slate-800"}`}>
                          {overdue ? "Overdue" : statusLabel(a.status)}
                        </span>
                      </TableCell>
                      <TableCell className="text-sm">
                        <div className="flex items-center gap-1">
                          {overdue && <Warning size={14} className="text-red-600" weight="fill" />}
                          {holderLabel(a)}
                        </div>
                        {a.status === "assigned" && a.account_id && (
                          <div className="text-xs opacity-60 truncate max-w-[180px]">@ {accountById(a.account_id)}</div>
                        )}
                      </TableCell>
                      <TableCell className="text-xs">
                        {a.issue_date && <div>Out: {fmtDate(a.issue_date)}</div>}
                        {a.expected_return_date && <div className={overdue ? "text-red-600 font-medium" : "opacity-70"}>Return: {fmtDate(a.expected_return_date)}</div>}
                      </TableCell>
                      <TableCell>{fmtINR(a.asset_value || 0)}</TableCell>
                      <TableCell className="text-right space-x-1 whitespace-nowrap">
                        {canManage && (a.status === "available" || a.status === "at_office") && <Button size="sm" variant="outline" onClick={() => openAssignDialog(a)} data-testid={`asset-assign-${a.id}`}>Assign</Button>}
                        {canManage && a.status === "assigned" && <Button size="sm" variant="outline" onClick={() => openAssignDialog(a)}>Reassign</Button>}
                        {canManage && (a.status === "assigned" || a.status === "at_office") && <Button size="sm" variant="outline" onClick={() => returnAsset(a)} data-testid={`asset-return-${a.id}`}>Return</Button>}
                        {canManage && <Button size="sm" variant="outline" onClick={() => { setEditingA(a); setFormA({ ...emptyA, ...a }); setOpenA(true); }}>Edit</Button>}
                        {canDelete && <Button size="sm" variant="outline" className="text-red-600" onClick={() => delA(a)}>Delete</Button>}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {filteredAssets.length === 0 && <TableRow><TableCell colSpan={8} className="text-center py-10 text-sm opacity-60">No assets to show.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="demo" className="mt-4 space-y-3">
          {canManageDemo && (
            <div className="flex justify-end">
              <Dialog open={openD} onOpenChange={setOpenD}>
                <DialogTrigger asChild><Button className="btn-brand" onClick={() => { setEditingD(null); setFormD(emptyD); }} data-testid="demo-new"><Plus size={16} className="mr-1.5" />New Demo Equipment</Button></DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader><DialogTitle>{editingD ? "Edit" : "New"} Demo Equipment</DialogTitle></DialogHeader>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Equipment Name *"><Input value={formD.equipment_name} onChange={(e) => setFormD({ ...formD, equipment_name: e.target.value })} /></Field>
                    <Field label="OEM *">
                      <Select value={formD.oem || "none"} onValueChange={(v) => setFormD({ ...formD, oem: v === "none" ? "" : v })}>
                        <SelectTrigger><SelectValue placeholder="OEM" /></SelectTrigger>
                        <SelectContent><SelectItem value="none">— None —</SelectItem>{oems.map((o) => <SelectItem key={o.id} value={o.name}>{o.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </Field>
                    <Field label="Product Code"><Input value={formD.product_code || ""} onChange={(e) => setFormD({ ...formD, product_code: e.target.value })} /></Field>
                    <Field label="Serial Number *"><Input value={formD.serial_number} onChange={(e) => setFormD({ ...formD, serial_number: e.target.value })} /></Field>
                    <Field label="Model Number"><Input value={formD.model_number || ""} onChange={(e) => setFormD({ ...formD, model_number: e.target.value })} /></Field>
                    <Field label="Received Date"><Input type="date" value={formD.received_date || ""} onChange={(e) => setFormD({ ...formD, received_date: e.target.value })} /></Field>
                    <Field label="Received From"><Input value={formD.received_from || ""} onChange={(e) => setFormD({ ...formD, received_from: e.target.value })} /></Field>
                    <Field label="Contact Person"><Input value={formD.contact_person || ""} onChange={(e) => setFormD({ ...formD, contact_person: e.target.value })} /></Field>
                    <Field label="Contact Number"><Input value={formD.contact_number || ""} onChange={(e) => setFormD({ ...formD, contact_number: e.target.value })} /></Field>
                    <Field label="Expected Return"><Input type="date" value={formD.expected_return_date || ""} onChange={(e) => setFormD({ ...formD, expected_return_date: e.target.value })} /></Field>
                    <Field label="Status">
                      <Select value={formD.status} onValueChange={(v) => setFormD({ ...formD, status: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>{DEMO_STATUS.map((s) => <SelectItem key={s} value={s}>{statusLabel(s)}</SelectItem>)}</SelectContent>
                      </Select>
                    </Field>
                    <div className="col-span-2"><Field label="Remarks"><Textarea value={formD.remarks || ""} onChange={(e) => setFormD({ ...formD, remarks: e.target.value })} /></Field></div>
                  </div>
                  <DialogFooter><Button variant="outline" onClick={() => setOpenD(false)}>Cancel</Button><Button className="btn-brand" onClick={saveD} data-testid="demo-save">Save</Button></DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          )}
          <Card className="p-0 overflow-hidden">
            <Table>
              <TableHeader><TableRow><TableHead>Equipment</TableHead><TableHead>OEM</TableHead><TableHead>Serial</TableHead><TableHead>Status</TableHead><TableHead>Return By</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
              <TableBody>
                {demo.map((d) => (
                  <TableRow key={d.id}>
                    <TableCell><div className="font-medium">{d.equipment_name}</div><div className="text-xs opacity-60">{d.product_code}</div></TableCell>
                    <TableCell>{d.oem}</TableCell>
                    <TableCell className="font-mono text-xs">{d.serial_number}</TableCell>
                    <TableCell><span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 capitalize">{statusLabel(d.status)}</span></TableCell>
                    <TableCell className="text-sm">{fmtDate(d.expected_return_date)}</TableCell>
                    <TableCell className="text-right space-x-1">
                      {canManageDemo && d.status !== "returned_to_oem" && <Button size="sm" variant="outline" onClick={() => returnToOEM(d)} data-testid={`demo-return-${d.id}`}>Return to OEM</Button>}
                      {canManageDemo && <Button size="sm" variant="outline" onClick={() => { setEditingD(d); setFormD({ ...emptyD, ...d }); setOpenD(true); }}>Edit</Button>}
                      {canDelete && <Button size="sm" variant="outline" className="text-red-600" onClick={() => delD(d)}>Delete</Button>}
                    </TableCell>
                  </TableRow>
                ))}
                {demo.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-10 text-sm opacity-60">No demo equipment yet.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Assign / Reassign Dialog */}
      <Dialog open={openAssign} onOpenChange={setOpenAssign}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Assign Item — {assignTarget?.product_name}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Field label="Where is this item right now? *">
                <div className="flex gap-2">
                  <Button size="sm" variant={formAssign.holder_type === "office" ? "default" : "outline"} className={formAssign.holder_type === "office" ? "btn-brand" : ""} onClick={() => setFormAssign({ ...formAssign, holder_type: "office", employee_id: "", account_id: "", opportunity_id: "" })} data-testid="assign-office">At Office</Button>
                  <Button size="sm" variant={formAssign.holder_type === "employee" ? "default" : "outline"} className={formAssign.holder_type === "employee" ? "btn-brand" : ""} onClick={() => setFormAssign({ ...formAssign, holder_type: "employee" })} data-testid="assign-employee">With Employee</Button>
                </div>
              </Field>
            </div>
            {formAssign.holder_type === "employee" && (
              <>
                <Field label="Employee *">
                  <Select value={formAssign.employee_id || ""} onValueChange={(v) => setFormAssign({ ...formAssign, employee_id: v })}>
                    <SelectTrigger><SelectValue placeholder="Pick employee" /></SelectTrigger>
                    <SelectContent>{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.full_name}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Account (optional)">
                  <Select value={formAssign.account_id || ""} onValueChange={(v) => setFormAssign({ ...formAssign, account_id: v, opportunity_id: "" })}>
                    <SelectTrigger><SelectValue placeholder="Pick account (optional)" /></SelectTrigger>
                    <SelectContent>{accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.company_name}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Opportunity (optional)">
                  <Select value={formAssign.opportunity_id || ""} onValueChange={(v) => setFormAssign({ ...formAssign, opportunity_id: v })} disabled={!formAssign.account_id}>
                    <SelectTrigger><SelectValue placeholder={formAssign.account_id ? "Pick opportunity (optional)" : "Select account first"} /></SelectTrigger>
                    <SelectContent>
                      {opportunities.filter((o) => !formAssign.account_id || o.account_id === formAssign.account_id).map((o) => (
                        <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              </>
            )}
            <Field label="Issue Date *"><Input type="date" value={formAssign.issue_date} onChange={(e) => setFormAssign({ ...formAssign, issue_date: e.target.value })} /></Field>
            {formAssign.holder_type === "employee" && (
              <Field label="Expected Return Date (optional)"><Input type="date" value={formAssign.expected_return_date || ""} onChange={(e) => setFormAssign({ ...formAssign, expected_return_date: e.target.value })} data-testid="assign-return-date" /></Field>
            )}
            <div className="col-span-2"><Field label="Purpose *"><Input value={formAssign.purpose} onChange={(e) => setFormAssign({ ...formAssign, purpose: e.target.value })} placeholder="e.g. POC / Customer demo" data-testid="assign-purpose" /></Field></div>
            <div className="col-span-2"><Field label="Remarks"><Textarea value={formAssign.remarks} onChange={(e) => setFormAssign({ ...formAssign, remarks: e.target.value })} /></Field></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenAssign(false)}>Cancel</Button>
            <Button className="btn-brand" onClick={submitAssign} data-testid="assign-save">Save Assignment</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
